import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { ReportCustomersResponse } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * The customer-wise summary §11.2 names by name.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * EVERY BUYER, NOT A PAGE OF THEM. `GET /reports/customers` deliberately does
 * not page: the point of the table is that its rows sum to the figure on the
 * card above it, and a total over page 1 of 4 is a smaller number than the truth
 * presented as the truth. A financial year in this business is a few dozen
 * buyers.
 *
 * THE NAMES ARE THE ONES ON THE PAPER. They come from each document's frozen
 * `customer_snapshot` (§4), so a buyer renamed last week does not rename the
 * bills he was sent — and the CA reconciling this list against the invoices in
 * his hand sees the same words on both.
 *
 * THREE MONEY COLUMNS, AND THE MIDDLE ONE IS WHY. "Billed" is what the bills
 * said; "Credited" is what the credit notes took back (POSITIVE, as the database
 * stores it — a minus sign here would be a sign the data does not carry, §17.3);
 * "Net" is the figure every other number on this screen agrees with. Showing
 * only the net would leave a customer's total quietly smaller than the invoices
 * he holds, with nothing on screen to explain the gap.
 *
 * THE TOTALS ROW IS THE SERVER'S FOLD, not a `reduce` over the rendered rows —
 * §6, and the one place a client-side sum could disagree with the card above.
 *
 * TWO LAYOUTS, BECAUSE THE OWNER READS THIS ON A PHONE (§11.1, §11.4). A
 * six-column table at 390px is a horizontal scroll he will not discover, so the
 * phone gets one row per buyer with the three figures that matter and the
 * desktop gets the book.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function ReportCustomerTable({
  data,
  loading,
}: {
  data: ReportCustomersResponse | undefined
  loading: boolean
}): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("reports.customerWise")}</CardTitle>
        <CardDescription>{t("reports.customerWiseHelp")}</CardDescription>
      </CardHeader>

      <CardContent>
        {loading || data === undefined ? (
          <div className="flex flex-col gap-3">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="flex items-center gap-3">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : data.data.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("reports.noBills")}</p>
        ) : (
          <>
            {/* Phone: one tappable row per buyer. */}
            <ul className="flex flex-col divide-y md:hidden" data-testid="report-customers-phone">
              {data.data.map((row) => (
                <li key={row.customerId}>
                  <Link
                    to={routes.invoicesForCustomerInPeriod(
                      row.customerId,
                      data.period.from,
                      data.period.to,
                    )}
                    className="hover:bg-accent/50 flex min-h-14 items-center gap-3 rounded-md px-2 py-2 transition-colors"
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium">
                        {row.customerName ?? t("lbl.customerUnknown")}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {t("reports.customerBills", { count: row.invoiceCount })}
                      </span>
                      {/* Only when there IS something credited — a permanent
                        * "Credited ₹0.00" line is furniture, and on most buyers
                        * it would be the loudest thing in the row. */}
                      {row.creditedPaise === 0 ? null : (
                        <span className="text-muted-foreground text-xs tabular-nums">
                          {t("reports.creditedRow", {
                            amount: formatRupees(row.creditedPaise),
                          })}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-sm font-medium tabular-nums">
                      {formatRupees(row.netBilledPaise)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Desktop: the book. */}
            <div className="hidden md:block" data-testid="report-customers-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    {/*
                      * NOT SORTABLE, and the plain headers say so.
                      * `GET /reports/customers` takes no `sort` parameter — the
                      * rows arrive largest net first, which is the order the
                      * question is asked in — and a header that looked sortable
                      * and was not would be worse than a plain one.
                      */}
                    <TableHead>{t("lbl.customer")}</TableHead>
                    <TableHead className="text-right">{t("reports.billsColumn")}</TableHead>
                    <TableHead className="text-right">{t("reports.billedColumn")}</TableHead>
                    <TableHead className="text-right">{t("reports.creditedColumn")}</TableHead>
                    <TableHead className="text-right">{t("reports.netColumn")}</TableHead>
                    <TableHead className="text-right">{t("reports.taxColumn")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((row) => (
                    <TableRow key={row.customerId} className="hover:bg-accent/50 relative">
                      <TableCell className="max-w-[28ch] truncate font-medium">
                        {/* `relative` on the row is load-bearing — the stretched
                          * overlay resolves against the nearest positioned
                          * ancestor, and without it every click on the page
                          * opens the last row. */}
                        <Link
                          to={routes.invoicesForCustomerInPeriod(
                            row.customerId,
                            data.period.from,
                            data.period.to,
                          )}
                          className="after:absolute after:inset-0 hover:underline"
                        >
                          {row.customerName ?? t("lbl.customerUnknown")}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.invoiceCount}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {formatRupees(row.billedPaise)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-right tabular-nums whitespace-nowrap">
                        {formatRupees(row.creditedPaise)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
                        {formatRupees(row.netBilledPaise)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {formatRupees(row.netTaxPaise)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-medium">{t("reports.totalRow")}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {data.totals.invoiceCount}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatRupees(data.totals.billedPaise)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatRupees(data.totals.creditedPaise)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
                      {formatRupees(data.totals.netBilledPaise)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatRupees(data.totals.netTaxPaise)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
